import { PUBS, eventRefs, type PubRef } from '../shared/library';
import { LANES, laneTitle, type SavedCriterion } from '../shared/lanes';
import { SCORE_LEGEND } from '../shared/domain';
import { fetchLaneCriteria, saveLaneCriteria } from './api';
import { el, textInput, textarea, toast } from './ui';

export function referenceChips(ids: string[]): HTMLElement {
  const box = el('div', { className: 'ref-list' });
  for (const id of ids) {
    const pub = PUBS.find((item) => item.id === id);
    const event = eventRefs().find((item) => item.id === id);
    const title = pub?.short || event?.code || id;
    const detail = pub ? pub.title.slice(pub.short.length).trim() : event?.title || '';
    box.append(
      el('span', { className: 'ref-chip' }, [
        el('strong', { textContent: title }),
        detail ? el('span', { textContent: detail }) : '',
      ])
    );
  }
  return box;
}

export function renderLibrary(root: HTMLElement): () => void {
  let alive = true;
  let catalog: Record<string, SavedCriterion[]> = {};
  const host = el('div', { className: 'stack' });
  root.append(host);
  host.append(el('p', { className: 'muted', textContent: 'Loading…' }));

  function home(): void {
    host.replaceChildren(
      el('h1', { textContent: 'Library' }),
      choice('Publications', () => publications()),
      choice('Evaluation Criteria', () => lanes())
    );
  }

  function publications(): void {
    const back = backButton(home);
    const cards = el('div', { className: 'stack' });
    for (const pub of PUBS) cards.append(pubCard(pub));
    host.replaceChildren(el('h1', { textContent: 'Publications' }), back, cards);
  }

  function pubCard(pub: PubRef): HTMLElement {
    const card = el('article', { className: 'feed-card lib-card' });
    const open = el('button', { type: 'button', className: 'feed-open' });
    open.append(el('strong', { textContent: pub.short }), el('span', { textContent: pub.title }));
    open.addEventListener('click', () => {
      const back = backButton(publications);
      host.replaceChildren(
        back,
        el('h1', { textContent: pub.short }),
        el('p', { className: 'lead', textContent: pub.title }),
        el('p', {
          textContent:
            'Course reference for the lane checklists. Score from the criteria published for the lane.',
        })
      );
    });
    card.append(open);
    return card;
  }

  function lanes(): void {
    const cards = el('div', { className: 'stack' });
    for (const lane of LANES) {
      const count = catalog[lane.id]?.length || 0;
      const card = el('article', { className: 'feed-card lib-card' });
      const open = el('button', { type: 'button', className: 'feed-open' });
      open.append(
        el('strong', { textContent: lane.title }),
        el('span', { className: 'muted', textContent: count ? `${count} E-PECLs` : 'No E-PECLs yet' })
      );
      open.addEventListener('click', () => editor(lane.id));
      card.append(open);
      cards.append(card);
    }
    host.replaceChildren(el('h1', { textContent: 'Evaluation Criteria' }), backButton(home), cards);
  }

  function editor(laneId: string): void {
    let items = (catalog[laneId] || []).map(copyCriterion);
    const list = el('div', { className: 'stack' });

    function paint(): void {
      list.replaceChildren();
      items.forEach((item, index) => list.append(epeclCard(item, index)));
      const add = el('button', {
        type: 'button',
        className: 'btn',
        textContent: 'Add another E-PECL',
      });
      add.addEventListener('click', () => {
        readItems();
        items.push({
          id: `epecl-${crypto.randomUUID().slice(0, 8)}`,
          title: '',
          prompt: '',
          lookFor: [''],
        });
        paint();
      });
      list.append(add);
    }

    function readItems(): void {
      const cards = [...list.querySelectorAll<HTMLElement>('[data-epecl]')];
      cards.forEach((card, index) => {
        const item = items[index];
        if (!item) return;
        const title = card.querySelector<HTMLInputElement>('[data-title]');
        if (title) item.title = title.value;
        item.lookFor = [...card.querySelectorAll<HTMLTextAreaElement>('[data-line]')].map(
          (node) => node.value
        );
      });
    }

    function epeclCard(item: SavedCriterion, index: number): HTMLElement {
      const card = el('section', { className: 'epecl-card', 'data-epecl': String(index) });
      const title = textInput({ value: item.title, placeholder: 'Performance item title' });
      title.dataset.title = '1';
      const remove = el('button', { type: 'button', className: 'btn btn-ghost', textContent: 'Delete' });
      remove.addEventListener('click', () => {
        readItems();
        items.splice(index, 1);
        paint();
      });
      const lines = el('div', { className: 'stack' });
      item.lookFor.forEach((line) => {
        const row = el('div', { className: 'line-edit' });
        const field = textarea('');
        field.classList.add('compact');
        field.dataset.line = '1';
        field.value = line;
        const drop = el('button', {
          type: 'button',
          className: 'btn btn-ghost',
          textContent: 'Remove',
          'aria-label': 'Remove line',
        });
        drop.addEventListener('click', () => {
          readItems();
          const current = items[index];
          if (!current) return;
          const at = [...lines.querySelectorAll('[data-line]')].indexOf(field);
          if (at >= 0) current.lookFor.splice(at, 1);
          paint();
        });
        row.append(field, drop);
        lines.append(row);
      });
      const addLine = el('button', { type: 'button', className: 'btn btn-ghost', textContent: 'Add a line' });
      addLine.addEventListener('click', () => {
        readItems();
        items[index]?.lookFor.push('');
        paint();
      });
      card.append(
        el('div', { className: 'epecl-top' }, [
          el('strong', { textContent: `E-PECL ${index + 1}` }),
          remove,
        ]),
        el('label', { className: 'field' }, [
          el('span', { className: 'field-label', textContent: 'Performance item' }),
          title,
        ]),
        el('div', { className: 'block' }, [
          el('span', { className: 'field-label', textContent: 'Evaluation Criteria and Attributes' }),
          lines,
          addLine,
        ]),
        el('p', { className: 'muted', textContent: SCORE_LEGEND }),
        previewScores(),
        el('label', { className: 'field' }, [
          el('span', { className: 'field-label', textContent: 'Evaluator Comments' }),
          textarea('Optional'),
        ])
      );
      return card;
    }

    const save = el('button', { type: 'button', className: 'btn btn-primary', textContent: 'Save' });
    save.addEventListener('click', () => {
      readItems();
      save.disabled = true;
      const payload = items
        .map((item) => ({
          ...item,
          title: item.title.trim(),
          lookFor: item.lookFor.map((line) => line.trim()).filter(Boolean),
        }))
        .filter((item) => item.title || item.lookFor.length);
      void saveLaneCriteria(laneId, payload)
        .then((saved) => {
          catalog[laneId] = saved;
          items = saved.map(copyCriterion);
          toast('Published to the lane evaluation', 'ok');
          paint();
        })
        .catch((err) => toast((err as Error).message, 'err'))
        .finally(() => {
          save.disabled = false;
        });
    });
    paint();
    host.replaceChildren(
      backButton(() => lanes()),
      el('h1', { textContent: laneTitle(laneId) }),
      list,
      save
    );
  }

  void fetchLaneCriteria()
    .then((data) => {
      if (!alive) return;
      catalog = data;
      home();
    })
    .catch((err) => {
      if (!alive) return;
      host.replaceChildren(el('p', { className: 'form-error', textContent: (err as Error).message }));
    });

  return () => {
    alive = false;
  };
}

function choice(label: string, open: () => void): HTMLElement {
  const btn = el('button', { type: 'button', className: 'dash-btn span-2 lib-choice' });
  btn.append(el('strong', { textContent: label }));
  btn.addEventListener('click', open);
  return btn;
}

function backButton(go: () => void): HTMLElement {
  const back = el('button', { type: 'button', className: 'btn btn-ghost', textContent: 'Back' });
  back.addEventListener('click', go);
  return back;
}

function copyCriterion(item: SavedCriterion): SavedCriterion {
  return {
    id: item.id,
    title: item.title,
    prompt: item.prompt || '',
    lookFor: [...(item.lookFor || [])],
  };
}

function previewScores(): HTMLElement {
  const scores = el('div', { className: 'score-grid' });
  let picked: number | 'na' | null = null;
  const paint = () => {
    scores.replaceChildren();
    for (const n of [1, 2, 3, 4, 5] as const) {
      const on = picked === n;
      const btn = el('button', {
        type: 'button',
        className: on ? 'score on' : 'score',
        textContent: String(n),
        'aria-pressed': on ? 'true' : 'false',
      });
      btn.addEventListener('click', () => {
        picked = n;
        paint();
      });
      scores.append(btn);
    }
    const naOn = picked === 'na';
    const na = el('button', {
      type: 'button',
      className: naOn ? 'score on' : 'score',
      textContent: 'N/A',
      'aria-pressed': naOn ? 'true' : 'false',
    });
    na.addEventListener('click', () => {
      picked = 'na';
      paint();
    });
    scores.append(na);
  };
  paint();
  return scores;
}

export function referenceLabel(id: string): string {
  const pub = PUBS.find((item) => item.id === id);
  if (pub) return pub.short;
  const event = eventRefs().find((item) => item.id === id);
  return event?.code || id;
}
